import { CircleCheck, Lock, OctagonX, X } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { TelaProtegidaCarregando } from '@/components/layout/tela-carregando';
import { Callout } from '@/components/patterns/callout';
import { DataTable, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterSelect } from '@/components/patterns/filter-bar';
import { EstadoErro, EstadoVazio, TabelaCarregando } from '@/components/patterns/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { BuildResumo, FiltrosFeed } from '@/data/types';
import { useFeed } from '@/hooks/use-feed';
import { useResource } from '@/hooks/use-resource';
import { useSessao } from '@/hooks/use-sessao';
import { podeHomologar } from '@/lib/capabilities';
import { formatarDataHora, formatarRelativo, plural } from '@/lib/format';
import { cn } from '@/lib/utils';
import { IncidenteSelo } from '@/components/patterns/incidente-badges';
import { useAvisoHomologacao } from '@/features/incidentes/aviso-homologacao';
import { MotivoDialog } from '@/features/versao/acoes-build';
import { aplicarStatus } from '@/features/versao/aplicar-status';
import { VersaoSheet } from '@/features/versao/VersaoSheet';

/**
 * Fila de homologação: tudo que está `nao_homologado`, agrupado por ramo, com ações
 * rápidas em linha e homologação em lote.
 *
 * Por padrão os builds noturnos de `master` ficam fora da fila — eles são
 * publicados todo dia e quase nunca são candidatos a release; o switch traz
 * de volta quem precisar avaliá-los.
 */

const FILTROS_BASE: FiltrosFeed = { status: ['nao_homologado'], limit: 100 };

export function HomologacaoPage() {
  const { roles, rolesCarregando } = useSessao();
  const acesso = podeHomologar(roles);

  if (rolesCarregando) {
    return (
      <TelaProtegidaCarregando
        eyebrow="Compilações"
        titulo="Homologação"
        descricao="Versões aguardando homologação, agrupadas por ramo. Homologue em linha ou marque várias e homologue em lote."
      />
    );
  }

  if (!acesso.permitido) {
    return (
      <PageBody>
        <Cabecalho />
        <Card>
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <Lock className="size-6 text-icon" aria-hidden="true" />
            <p className="text-card-title">Homologação bloqueada</p>
            <p className="max-w-md text-body text-on-surface-muted">{acesso.motivo} Peça a capability ao time de SRE.</p>
          </div>
        </Card>
      </PageBody>
    );
  }

  return <FilaHomologacao />;
}

function Cabecalho() {
  return (
    <PageHeader
      eyebrow="Compilações"
      titulo="Homologação"
      descricao="Versões aguardando homologação, agrupadas por ramo. Homologue em linha ou marque várias e homologue em lote."
    />
  );
}

function FilaHomologacao() {
  const [params, setParams] = useSearchParams();
  const versaoAberta = params.get('versao');
  const [branch, setBranch] = React.useState<string | null>(null);
  const [incluirMaster, setIncluirMaster] = React.useState(false);
  const [selecionadas, setSelecionadas] = React.useState<Set<string>>(new Set());
  const [emLote, setEmLote] = React.useState(false);
  const [falhasLote, setFalhasLote] = React.useState<Array<{ versao: string; mensagem: string }>>([]);
  const [linhaEmAcao, setLinhaEmAcao] = React.useState<string | null>(null);
  const [motivoPara, setMotivoPara] = React.useState<BuildResumo | null>(null);
  const aviso = useAvisoHomologacao();

  const filtros = React.useMemo<FiltrosFeed>(() => ({ ...FILTROS_BASE, branch }), [branch]);
  const feed = useFeed(filtros);
  const ramos = useResource((signal) => dataSource.listarRamos(undefined, signal), []);

  const aguardando = feed.itens.filter(
    (item) => item.status === 'nao_homologado' && (incluirMaster || branch === 'master' || item.branch !== 'master'),
  );
  const ocultasMaster = feed.itens.filter((item) => item.status === 'nao_homologado' && item.branch === 'master').length;

  const grupos = React.useMemo(() => {
    const mapa = new Map<string, BuildResumo[]>();
    for (const item of aguardando) mapa.set(item.ramo, [...(mapa.get(item.ramo) ?? []), item]);
    return [...mapa.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [aguardando]);

  // Seleção nunca guarda versão que saiu da fila (homologada no Sheet, por ex.).
  const visiveis = new Set(aguardando.map((item) => item.versao));
  const selecaoValida = [...selecionadas].filter((versao) => visiveis.has(versao));

  const opcoesBranch = [...new Set((ramos.dados ?? []).flatMap((item) => item.branches))]
    .sort((a, b) => (a === 'master' ? 1 : b === 'master' ? -1 : b.localeCompare(a)))
    .map((valor) => ({ valor, rotulo: valor }));

  function abrir(build: BuildResumo) {
    setParams((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('versao', build.versao);
      return proximo;
    });
  }

  function alternar(versao: string, marcado: boolean) {
    setSelecionadas((atual) => {
      const proximo = new Set(atual);
      if (marcado) proximo.add(versao);
      else proximo.delete(versao);
      return proximo;
    });
  }

  async function homologarUma(build: BuildResumo) {
    setLinhaEmAcao(build.versao);
    try {
      // Versão com incidente aberto/mitigado: não bloqueia, mas pede confirmação.
      const confirmado =
        build.incidentes_abertos === 0 ? true : await aviso.confirmar([{ versao: build.versao, produto: build.produto }]);
      if (!confirmado) return;
      await aplicarStatus(build, 'homologado', null);
      toast.success(`Versão ${build.versao} homologada.`);
    } catch (causa) {
      toast.error(`Não foi possível homologar ${build.versao}: ${mensagemDoErro(causa) ?? (causa as Error).message}`);
    } finally {
      setLinhaEmAcao(null);
    }
  }

  async function homologarSelecionadas() {
    const alvo = aguardando.filter((item) => selecaoValida.includes(item.versao));
    // Um diálogo só para o lote, listando os incidentes de cada versão afetada.
    const comIncidente = alvo.filter((item) => item.incidentes_abertos > 0);
    if (comIncidente.length > 0) {
      const confirmado = await aviso.confirmar(comIncidente.map((item) => ({ versao: item.versao, produto: item.produto })));
      if (!confirmado) return;
    }
    setEmLote(true);
    setFalhasLote([]);
    const falhas: Array<{ versao: string; mensagem: string }> = [];
    // Em sequência, não em paralelo: cada homologação grava evento e a API
    // pode recusar uma (409) sem que isso derrube as outras.
    for (const build of alvo) {
      try {
        await aplicarStatus(build, 'homologado', null);
      } catch (causa) {
        falhas.push({ versao: build.versao, mensagem: mensagemDoErro(causa) ?? (causa as Error).message });
      }
    }
    setEmLote(false);
    setSelecionadas(new Set(falhas.map((item) => item.versao)));
    setFalhasLote(falhas);
    const sucesso = alvo.length - falhas.length;
    if (sucesso > 0) toast.success(`${plural(sucesso, 'versão homologada', 'versões homologadas')}.`);
    if (falhas.length > 0) toast.error(`${plural(falhas.length, 'versão não foi homologada', 'versões não foram homologadas')}.`);
  }

  const colunas: Array<Coluna<BuildResumo>> = [
    {
      id: 'versao',
      cabecalho: 'Versão',
      ordenarPor: (item) => item.publicado_em,
      celula: (item) => (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={() => abrir(item)}
            className="rounded-button font-mono font-bold text-primary underline-offset-4 hover:underline"
            aria-label={`Abrir o detalhe da versão ${item.versao}`}
          >
            {item.versao}
          </button>
          <IncidenteSelo
            quantidade={item.incidentes_abertos}
            severidade={item.incidente_severidade_max}
            versao={item.versao}
          />
        </span>
      ),
    },
    {
      id: 'branch',
      cabecalho: 'Branch',
      ordenarPor: (item) => item.branch,
      celula: (item) => <span className="font-mono">{item.branch}</span>,
    },
    {
      id: 'publicado',
      cabecalho: 'Publicada',
      ordenarPor: (item) => item.publicado_em,
      celula: (item) => (
        <time dateTime={item.publicado_em} title={formatarDataHora(item.publicado_em)}>
          {formatarRelativo(item.publicado_em)}
        </time>
      ),
    },
    {
      id: 'artefatos',
      cabecalho: 'Artefatos',
      alinhamento: 'right',
      secundaria: true,
      ordenarPor: (item) => item.total_artefatos,
      celula: (item) => <span className="tabular-nums">{item.total_artefatos}</span>,
    },
    {
      id: 'acoes',
      cabecalho: <span className="sr-only">Ações</span>,
      alinhamento: 'right',
      largura: '132px',
      celula: (item) => (
        <div className="flex justify-end gap-1">
          <AcaoRapida
            rotulo={`Homologar ${item.versao}`}
            dica="Homologar"
            onClick={() => void homologarUma(item)}
            disabled={linhaEmAcao !== null || emLote}
            className="text-success"
          >
            <CircleCheck aria-hidden="true" />
          </AcaoRapida>
          <AcaoRapida
            rotulo={`Reprovar ${item.versao}`}
            dica="Reprovar"
            onClick={() => setMotivoPara(item)}
            disabled={linhaEmAcao !== null || emLote}
            className="text-danger"
          >
            <OctagonX aria-hidden="true" />
          </AcaoRapida>
        </div>
      ),
    },
  ];

  return (
    <PageBody>
      <Cabecalho />

      <Card as="section" aria-label="Filtros da fila" className="px-4 py-3">
        <FilterBar>
          <FilterSelect
            id="fila-branch"
            label="Branch"
            valor={branch}
            aoMudar={setBranch}
            opcoes={opcoesBranch}
            rotuloTodos="Todas"
          />
          {branch === null ? (
            <label className="flex h-10 items-center gap-2 text-label">
              <Switch checked={incluirMaster} onCheckedChange={setIncluirMaster} aria-label="Incluir builds de master" />
              Incluir builds de master
              {!incluirMaster && ocultasMaster > 0 ? (
                <span className="font-normal text-on-surface-muted">({ocultasMaster} ocultos)</span>
              ) : null}
            </label>
          ) : null}
        </FilterBar>
      </Card>

      {falhasLote.length > 0 ? (
        <Callout tom="erro" kicker="Homologação em lote">
          <ul className="flex flex-col gap-1">
            {falhasLote.map((falha) => (
              <li key={falha.versao}>
                <span className="font-mono font-bold">{falha.versao}</span>: {falha.mensagem}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {feed.estado === 'carregando' ? (
        <Card>
          <TabelaCarregando colunas={5} />
        </Card>
      ) : feed.estado === 'erro' ? (
        <Card>
          <EstadoErro erro={feed.erro} aoTentarDeNovo={feed.recarregar} />
        </Card>
      ) : grupos.length === 0 ? (
        <Card>
          <EstadoVazio
            titulo="Nada aguardando homologação"
            descricao={
              ocultasMaster > 0 && !incluirMaster
                ? `Nenhuma versão de release aguardando homologação. Há ${plural(ocultasMaster, 'build de master oculto', 'builds de master ocultos')}.`
                : 'Todas as versões publicadas já foram avaliadas.'
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {grupos.map(([ramo, itens]) => (
            <TabelaPainel key={ramo} titulo={`Ramo ${ramo}`} contagem={plural(itens.length, 'aguardando', 'aguardando')}>
              <DataTable
                itens={itens}
                colunas={colunas}
                chave={(item) => item.versao}
                estado="pronto"
                rotulo={`Versões aguardando homologação do ramo ${ramo}`}
                rotuloSelecionarTodas={`Selecionar todas as aguardando homologação do ramo ${ramo}`}
                vazio={{ titulo: 'Nada aguardando homologação', descricao: 'Sem versões aguardando homologação neste ramo.' }}
                selecionadas={selecionadas}
                aoAlternarSelecao={alternar}
                aoAlternarTodas={(marcado) => itens.forEach((item) => alternar(item.versao, marcado))}
              />
            </TabelaPainel>
          ))}
          {feed.temMais ? (
            <div className="flex justify-center">
              <Button variant="outline" loading={feed.carregandoMais} onClick={feed.carregarMais}>
                Carregar mais versões
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {selecaoValida.length > 0 ? (
        <div
          role="region"
          aria-label="Ações em lote"
          className="sticky bottom-4 z-20 mx-auto flex w-full max-w-xl flex-wrap items-center gap-3 rounded-card border border-primary-border bg-bg-surface px-4 py-3 shadow-lg"
        >
          <span className="flex-1 text-body font-bold">
            {plural(selecaoValida.length, 'versão selecionada', 'versões selecionadas')}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setSelecionadas(new Set())} disabled={emLote}>
            <X aria-hidden="true" />
            Limpar
          </Button>
          <Button variant="primary" size="sm" loading={emLote} onClick={() => void homologarSelecionadas()}>
            {emLote ? null : <CircleCheck aria-hidden="true" />}
            Homologar {selecaoValida.length}
          </Button>
        </div>
      ) : null}

      {aviso.dialogo}

      <MotivoDialog
        build={motivoPara ?? { versao: '', produto: '' }}
        acao={motivoPara ? { destino: 'reprovado', rotulo: 'Reprovar', democao: false } : null}
        aoFechar={() => setMotivoPara(null)}
        aoConcluir={() => setMotivoPara(null)}
      />

      <VersaoSheet
        versao={versaoAberta}
        aoFechar={() =>
          setParams((atual) => {
            const proximo = new URLSearchParams(atual);
            proximo.delete('versao');
            return proximo;
          })
        }
      />
    </PageBody>
  );
}

function AcaoRapida({
  rotulo,
  dica,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { rotulo: string; dica: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" className={cn('size-8', className)} aria-label={rotulo} {...props}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{dica}</TooltipContent>
    </Tooltip>
  );
}
